// src/pages/ArtworkEdit.jsx
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";
import IconButtons from "../components/shared/IconButtons";

import ArtworkDetailTab from "../components/artwork/tabs/ArtworkDetailTab";
import ArtworkSpecificationsTab from "../components/artwork/tabs/ArtworkSpecificationsTab";
import ArtworkProvenanceAcquisitionTab from "../components/artwork/tabs/ArtworkProvenanceAcquisitionTab";
import ArtworkDocumentsTab from "../components/artwork/tabs/ArtworkDocumentsTab";
import ZipImportButton from "../components/artwork/ZipImportButton";
import ArtworkLocationTab from "../components/artwork/tabs/ArtworkLocationTab";
import ArtworkNFTTab from "../components/artwork/tabs/ArtworkNFTTab";
import ArtworkComparables from "../components/artwork/tabs/ArtworkComparables";

import ArtworkImageManager from "../components/artwork/ArtworkImageManager";
import LocationModal from "../components/artwork/modals/LocationModal";
import DurationModal from "../components/artwork/modals/DurationModal";
import FrameSizeModal from "../components/artwork/modals/FrameSizeModal";
import PaperSizeModal from "../components/artwork/modals/PaperSizeModal";
import DocumentModal from "../components/artwork/modals/DocumentModal";
import ImageUploadModal from "../components/artwork/modals/ImageUploadModal";
import UseMyPhoneModal from "../components/artwork/modals/UseMyPhoneModal";
import { normalizeMedium } from "../utils/normalizeMedium";

// -------------------------------------------------------------
// Helpers
// -------------------------------------------------------------

function normalizeForDetection(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function isTimeBasedArtwork(data = {}) {
  const haystack = normalizeForDetection([
    data.medium,
    data.medium_category,
    data.mediumCategory,
    data.medium_family_key,
    data.typeOfArt,
    data.type_of_art,
  ].filter(Boolean).join(" "));

  if (haystack.includes("papier") || haystack.includes("paper")) return false;

  return [
    "video",
    "vidéo",
    "film",
    "time based",
    "time-based",
    "sonore",
    "sound",
    "performance",
    "installation",
    "digital",
    "numerique",
    "numérique",
  ].some((keyword) => haystack.includes(normalizeForDetection(keyword)));
}

function isPaperArtwork(data = {}) {
  const haystack = normalizeForDetection([
    data.medium,
    data.medium_category,
    data.mediumCategory,
    data.medium_family_key,
    data.typeOfArt,
    data.type_of_art,
  ].filter(Boolean).join(" "));

  return ["papier", "paper", "drawing", "dessin", "print", "estampe", "gravure"].some((keyword) =>
    haystack.includes(normalizeForDetection(keyword))
  );
}

function ensureSpecObject(data = {}) {
  return data.specifications && typeof data.specifications === "object"
    ? data.specifications
    : {};
}

function safeParse(value, fallback) {
  if (value == null) return fallback;
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch (e) {
    console.warn("[ArtworkEdit] JSON parse error:", value, e);
    return fallback;
  }
}

function isBlank(value) {
  return (
    value === undefined ||
    value === null ||
    (typeof value === "string" && value.trim() === "")
  );
}

function firstNonBlank(...values) {
  for (const value of values) {
    if (!isBlank(value)) return value;
  }
  return null;
}

function toNumberOrNull(value) {
  if (isBlank(value)) return null;
  const n = Number(String(value).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

function formatDimensionsString({ height, width, depth, unit = "cm" } = {}) {
  // ARTKIV : ne jamais réordonner automatiquement hauteur/largeur.
  // Certaines œuvres sont volontairement plus hautes que larges.
  const h = toNumberOrNull(height);
  const w = toNumberOrNull(width);
  const d = toNumberOrNull(depth);
  const u = unit || "cm";

  if (h === null && w === null && d === null) return "";

  const parts = [];
  if (h !== null) parts.push(String(h));
  if (w !== null) parts.push(String(w));
  if (d !== null) parts.push(String(d));

  return `${parts.join(" x ")}${u ? ` ${u}` : ""}`;
}

function parseDimensionsString(dimensions) {
  if (!dimensions || typeof dimensions !== "string") return {};

  const cleaned = dimensions
    .toLowerCase()
    .replace(/cm|mm|inches|inch|"/g, "")
    .replace(/×/g, "x")
    .replace(/\s+/g, "")
    .trim();

  if (!cleaned) return {};

  const parts = cleaned
    .split("x")
    .map((p) => parseFloat(p))
    .filter((n) => !Number.isNaN(n));

  const [height, width, depth] = parts;
  return {
    height: height || null,
    width: width || null,
    depth: depth || null,
  };
}


function normalizeAcquisitionType(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return null;

  const map = {
    purchased: "purchase",
    purchase: "purchase",
    achat: "purchase",
    achetee: "purchase",
    "achetée": "purchase",
    acquired: "purchase",
    acquisition: "purchase",
    artist: "artist",
    artiste: "artist",
    direct_artist: "artist",
    artist_direct: "artist",
    galerie: "gallery",
    gallery: "gallery",
    dealer: "gallery",
    auction: "auction",
    enchere: "auction",
    "enchères": "auction",
    "vente aux encheres": "auction",
    "vente aux enchères": "auction",
    donated: "donation",
    donation: "donation",
    don: "donation",
    gift: "donation",
    inheritance: "inheritance",
    heritage: "inheritance",
    "héritage": "inheritance",
    succession: "inheritance",
    private: "private",
    particulier: "private",
    private_sale: "private",
    other: "other",
    autre: "other",
  };

  return map[raw] || raw;
}

function cleanDataBeforeSubmit(data) {
  const cleaned = { ...data };

  // -----------------------------
  // 🎨 MEDIUM / TECHNIQUE / CATEGORY
  // -----------------------------
  // La base Supabase est en snake_case. Certains composants UI peuvent encore
  // produire mediumCategory : on convertit ici puis on supprime l'alias camelCase.
  const uiMediumCategory =
    cleaned.medium_category ||
    cleaned.mediumCategory ||
    cleaned.medium_family_key ||
    "";

  const mediumSource =
    cleaned.medium ||
    cleaned.medium_original ||
    cleaned.technique ||
    cleaned.typeOfArt ||
    cleaned.type_of_art ||
    "";

  const mediumNormalized = normalizeMedium(mediumSource);

  if (mediumNormalized.medium) {
    cleaned.medium = mediumNormalized.medium;
    cleaned.medium_original = cleaned.medium_original || mediumNormalized.medium;
  }

  const normalizedCategory = String(
    uiMediumCategory || mediumNormalized.medium_category || ""
  ).trim();

  if (normalizedCategory) {
    cleaned.medium_category = normalizedCategory;
  } else if (mediumNormalized.medium_category) {
    cleaned.medium_category = mediumNormalized.medium_category;
  }

  cleaned.medium_family_key =
    cleaned.medium_family_key || cleaned.medium_category || null;
  cleaned.medium_normalized_key =
    cleaned.medium_normalized_key || cleaned.medium_category || null;

  if (!cleaned.medium_normalization_status) {
    cleaned.medium_normalization_status = mediumNormalized.needs_ai_review
      ? "unmatched"
      : "auto";
  }

  if (mediumNormalized.confidence != null && !cleaned.medium_normalization_confidence) {
    cleaned.medium_normalization_confidence = mediumNormalized.confidence;
  }

  delete cleaned.mediumCategory;
  delete cleaned.technique;
  delete cleaned.medium_ai_review_needed;

  // champs UI internes — ne jamais envoyer à Supabase

  delete cleaned.isShared;
  delete cleaned.ownerId;
  delete cleaned.owner_id;
  delete cleaned.canEdit;
  delete cleaned._collabId;
  delete cleaned._collabCreatedBy;
  delete cleaned.location;
  delete cleaned.location_label;
  delete cleaned.primary_image_url;
  delete cleaned.framed_unit;
  delete cleaned.paper_unit;
  delete cleaned._yearNormalizedToJan1;

  // purge générique des champs techniques de permission / vues
  Object.keys(cleaned).forEach((key) => {
    if (
      key.startsWith("can_") ||
      key.startsWith("permission_") ||
      key.startsWith("access_") ||
      key.startsWith("shared_") ||
      key.endsWith("_visible")
    ) {
      delete cleaned[key];
    }
  });

  // Normalisation acquisitionDetails
  // -> colonnes plates acquisition_* ET objet jsonb enrichi.
  // Règle métier : acquisition_source décrit la source/le vendeur générique.
  // auction_house est uniquement la maison de vente. Les deux ne sont pas fusionnés.
  if (cleaned.acquisitionDetails && typeof cleaned.acquisitionDetails === "object") {
    const acq = cleaned.acquisitionDetails || {};

    const acqDate = firstNonBlank(
      cleaned.acquisition_date,
      acq.date,
      acq.acquisition_date
    );
    const acqPrice = firstNonBlank(
      cleaned.acquisition_price,
      acq.price,
      acq.purchasePrice,
      acq.acquisition_price,
      acq.lot_total,
      acq.donation_value,
      acq.declared_value,
      cleaned.price
    );
    const acqType = normalizeAcquisitionType(
      firstNonBlank(cleaned.acquisition_type, acq.type, acq.acquisition_type, acq.sale_type)
    );
    const acqSource = firstNonBlank(
      cleaned.acquisition_source,
      acq.source,
      acq.acquisition_source,
      acq.seller,
      acq.donor
    );

    const isAuction = acqType === "auction";

    const auctionHouse = isAuction ? (cleaned.auction_house || acq.auction_house || null) : null;
    const auctionSaleName = isAuction ? (cleaned.auction_sale_name || acq.auction_sale_name || null) : null;
    const auctionSaleDate = isAuction ? (cleaned.auction_sale_date || acq.auction_sale_date || null) : null;
    const auctionLotNumber = isAuction
      ? (cleaned.auction_lot_number || acq.auction_lot_number || acq.lot_number || null)
      : null;
    const auctionHammerPrice = isAuction
      ? (cleaned.auction_hammer_price ?? acq.auction_hammer_price ?? acq.hammer_price ?? null)
      : null;
    const auctionFees = isAuction
      ? (cleaned.auction_fees ?? acq.auction_fees ?? null)
      : null;

    if (!isBlank(acqDate)) cleaned.acquisition_date = acqDate;
    else delete cleaned.acquisition_date;

    cleaned.acquisition_price = acqPrice;
    cleaned.acquisition_type = acqType;
    cleaned.acquisition_source = acqSource;

    cleaned.auction_house = auctionHouse;
    cleaned.auction_sale_name = auctionSaleName;
    cleaned.auction_sale_date = auctionSaleDate;
    cleaned.auction_lot_number = auctionLotNumber;
    cleaned.auction_hammer_price = auctionHammerPrice;
    cleaned.auction_fees = auctionFees;

    cleaned.acquisitionDetails = {
      ...acq,
      ...(!isBlank(acqDate) ? { date: acqDate, acquisition_date: acqDate } : {}),
      ...(!isBlank(acqPrice) ? { price: acqPrice, purchasePrice: acqPrice, acquisition_price: acqPrice, lot_total: acqPrice } : {}),
      ...(!isBlank(acqType) ? { type: acqType, acquisition_type: acqType, sale_type: acqType } : {}),
      ...(!isBlank(acqSource) ? { source: acqSource, acquisition_source: acqSource } : {}),
      ...(isAuction ? {
        auction_house: auctionHouse,
        auction_sale_name: auctionSaleName,
        auction_sale_date: auctionSaleDate,
        auction_lot_number: auctionLotNumber,
        lot_number: auctionLotNumber,
        auction_hammer_price: auctionHammerPrice,
        hammer_price: auctionHammerPrice,
        auction_fees: auctionFees,
      } : {}),
    };
    // NE PAS stringify : acquisitionDetails est jsonb, Supabase attend un objet.
  }

  // -------------------------------------------------------------
  // DIMENSIONS — SQL = source de vérité, specifications.dimensions = miroir
  // -------------------------------------------------------------
  // Les imports XLS/OCR peuvent remplir specifications.dimensions.
  // Mais la fiche œuvre doit toujours reposer sur les colonnes SQL réelles.
  // Donc : SQL first, legacy second, JSON fallback, puis resynchronisation du miroir JSON.
  {
    const specs =
      cleaned.specifications && typeof cleaned.specifications === "object"
        ? cleaned.specifications
        : safeParse(cleaned.specifications, {});

    const jsonDims = specs?.dimensions || {};
    const parsedDims = parseDimensionsString(cleaned.dimensions || "");

    const unit =
      firstNonBlank(cleaned.dimensions_unit, cleaned.unit, jsonDims.unit, "cm") || "cm";

    const height = toNumberOrNull(
      firstNonBlank(cleaned.dimensions_height, jsonDims.height, parsedDims.height, cleaned.height)
    );

    const width = toNumberOrNull(
      firstNonBlank(cleaned.dimensions_width, jsonDims.width, parsedDims.width, cleaned.width)
    );

    const depth = toNumberOrNull(
      firstNonBlank(cleaned.dimensions_depth, jsonDims.depth, parsedDims.depth, cleaned.depth)
    );

    cleaned.dimensions_unit = unit;
    cleaned.dimensions_height = height;
    cleaned.dimensions_width = width;
    cleaned.dimensions_depth = depth;

    // Colonnes legacy conservées pour compatibilité lecture/recherche.
    cleaned.height = height;
    cleaned.width = width;
    cleaned.depth = depth;
    cleaned.unit = unit;

    cleaned.dimensions = formatDimensionsString({ height, width, depth, unit });

    cleaned.specifications = {
      ...(specs || {}),
      dimensions: {
        ...jsonDims,
        unit,
        height,
        width,
        depth,
      },
    };

    if (specs?.framedSize) {
      const f = specs.framedSize;
      cleaned.framed_height = toNumberOrNull(f.height);
      cleaned.framed_width = toNumberOrNull(f.width);
      cleaned.framed_depth = toNumberOrNull(f.depth);
    }

    if (specs?.paperSize) {
      const p = specs.paperSize;
      cleaned.paper_height = toNumberOrNull(p.height);
      cleaned.paper_width = toNumberOrNull(p.width);
    }

    if (specs?.duration != null) {
      cleaned.duration = specs.duration;
    }
  }

  // Sérialisation :
  // - champs TEXT qui stockent du JSON -> stringify si objet
  // - champs JSONB natifs -> s'assurer que c'est un objet (parser si string)
  // - NE JAMAIS stringify un champ jsonb -> Supabase 22023 "cannot set path in scalar"

  // Champs TEXT : documents, provenance -> stringify
  for (const key of ["documents", "provenance"]) {
    const val = cleaned[key];
    if (val === undefined) continue;
    if (Array.isArray(val) || (val && typeof val === "object")) {
      cleaned[key] = JSON.stringify(val);
    }
  }

  // Champs JSONB : images, specifications, acquisitionDetails,
  // creationDate, proposalHistory, blockchainMetadata, visual_analysis
  // -> envoyer comme objet natif (parser si c'est une string)
  const jsonbFields = [
    "images", "specifications", "acquisitionDetails",
    "creationDate", "proposalHistory", "blockchainMetadata", "visual_analysis",
  ];
  for (const key of jsonbFields) {
    const val = cleaned[key];
    if (val === undefined || val === null) continue;
    if (typeof val === "string") {
      try { cleaned[key] = JSON.parse(val); } catch { cleaned[key] = null; }
    }
  }

  // Sécurité Supabase : champs numeric/date vides -> null.
  // Sinon Postgres refuse "" dans numeric/date.
  [
    "price",
    "value",
    "estimated_value",
    "acquisition_price",
    "acquisition_price_eur",
    "acquisition_exchange_rate",
    "auction_hammer_price",
    "auction_fees",
    "sale_price",
    "price_low",
    "price_high",
    "valuation_estimated_value",
    "valuation_confidence_score",
    "valuation_market_value",
    "valuation_consensus",
    "current_estimated_value",
  ].forEach((key) => {
    if (cleaned[key] === "" || cleaned[key] === undefined) {
      cleaned[key] = null;
    } else if (cleaned[key] != null && typeof cleaned[key] !== "number") {
      const n = Number(String(cleaned[key]).replace(/\s/g, "").replace(",", "."));
      cleaned[key] = Number.isFinite(n) ? n : null;
    }
  });

  // Ne jamais écraser une date OCR / DB existante avec une chaîne vide.
  // Si l'UI n'a pas de valeur, on retire la clé du payload au lieu d'envoyer null.
  ["acquisition_date", "auction_sale_date"].forEach((key) => {
    if (cleaned[key] === "" || cleaned[key] === undefined || cleaned[key] === null) {
      delete cleaned[key];
    }
  });

  [
    "last_valuation_date",
    "expert_date",
    "visited_at",
  ].forEach((key) => {
    if (cleaned[key] === "" || cleaned[key] === undefined) {
      cleaned[key] = null;
    }
  });

// -----------------------------
// 🎨 SIGNATURE - STRUCTURE PROPRE (SCALABLE)
// -----------------------------
if (
  cleaned.signature_details ||
  cleaned.authentication_details ||
  cleaned.signature_notes
) {
  // JSONB (source de vérité riche)
  cleaned.signature_info = {
    signature_details: cleaned.signature_details || null,
    authentication_details: cleaned.authentication_details || null,
    signature_notes: cleaned.signature_notes || null,
  };

  // Colonnes SQL (lisible + filtrable)
  cleaned.signature_desc =
    cleaned.signature_details || cleaned.signature_desc || null;

  cleaned.certificate_authenticity =
    cleaned.authentication_details || cleaned.certificate_authenticity || null;

  cleaned.certificate_auth = Boolean(cleaned.authentication_details);
}

// Nettoyage champs UI (sinon crash Supabase)
delete cleaned.signature_details;
delete cleaned.authentication_details;
delete cleaned.signature_notes;

  // -----------------------------
  // ✅ CHECK CONSTRAINTS Supabase
  // -----------------------------
  const enumOrNull = (value, allowed, fallback = null) => {
    if (value === "" || value === undefined || value === null) return fallback;
    const v = String(value).trim();
    return allowed.includes(v) ? v : fallback;
  };

  cleaned.medium_normalization_status = enumOrNull(
    cleaned.medium_normalization_status,
    ["pending", "auto", "reviewed", "custom", "unmatched"],
    "pending"
  );

  cleaned.ownership_status = enumOrNull(
    cleaned.ownership_status,
    ["full", "usufruct", "bare_ownership"],
    "full"
  );

  cleaned.storage_risk = enumOrNull(
    cleaned.storage_risk,
    ["faible", "moyen", "élevé"],
    null
  );

  cleaned.artist_career_stage = enumOrNull(
    cleaned.artist_career_stage,
    ["emerging", "mid-career", "established", "blue-chip"],
    null
  );

  cleaned.artist_gender = enumOrNull(
    cleaned.artist_gender,
    ["m", "f", "other", "unknown"],
    null
  );

  cleaned.currency = enumOrNull(
    cleaned.currency,
    ["EUR", "USD", "GBP", "CHF", "JPY", "CNY", "HKD"],
    "EUR"
  );

  cleaned.market_demand = enumOrNull(
    cleaned.market_demand,
    ["low", "medium", "high", "very_high"],
    null
  );

  console.log("[ArtworkEdit] payload clean envoyé Supabase", cleaned);

  return cleaned;
}

// Limites d’images selon rôle / plan
function getMaxImagesForProfile(profile) {
  const roleRaw = (
    profile?.role ||
    profile?.user_role ||
    profile?.profile_role ||
    ""
  )
    .toString()
    .toLowerCase();
  const planRaw = (
    profile?.subscription_plan ||
    profile?.plan ||
    profile?.plan_type ||
    ""
  )
    .toString()
    .toLowerCase();

  const isAdvisor =
    roleRaw.includes("advisor") || roleRaw.includes("art_advisor");
  const isPremium = planRaw.includes("premium");

  // - user, plan standard : 5
  // - user, plan premium : 10
  // - art advisor, plan standard : 10
  // - art advisor, plan premium : illimité
  if (isAdvisor) {
    if (isPremium) return Infinity;
    return 10;
  }

  if (isPremium) return 10;
  return 5;
}

// -------------------------------------------------------------
// Component
// -------------------------------------------------------------
const ArtworkEdit = () => {
  const { t } = useTranslation();
  const { artworkId } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth() || {};

  const [artwork, setArtwork] = useState(null);
  const [formData, setFormData] = useState(null);
  const initialRef = useRef(null);
  const collabIdRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [locations, setLocations] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [showFrameSizeModal, setShowFrameSizeModal] = useState(false);
  const [showPaperSizeModal, setShowPaperSizeModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [showPhoneImg, setShowPhoneImg] = useState(false); // passerelle QR : verso/signature/détail
  const [durationModalAlreadyPrompted, setDurationModalAlreadyPrompted] = useState(false);
  const [error, setError] = useState(null);

  // Rôle expert : seul role="expert" voit l'onglet Expertise CUP
  const isExpert = (
    profile?.role === "expert" ||
    profile?.user_role === "expert" ||
    profile?.role === "art_advisor" ||
    profile?.plan === "art_advisor" ||
    profile?.plan === "art_advisor_premium"
  );

  const tabs = [
    { name: t("artworkEdit.edit.tabDetails"), id: "details" },
    { name: t("artworkEdit.edit.tabSpecifications"), id: "specifications" },
    { name: t("artworkEdit.edit.tabProvenance"), id: "provenance" },
    { name: t("artworkEdit.edit.tabDocuments"), id: "documents" },
    ...(!formData?._collabId ? [{ name: t("artworkEdit.edit.tabLocation"), id: "location" }] : []),
    { name: t("artworkEdit.edit.tabNft"), id: "nft" },
    // Onglet Expertise CUP — réservé aux experts/art advisors
    ...(isExpert ? [{ name: t("artworkEdit.edit.tabExpertise"), id: "expertise" }] : []),
  ];


  useEffect(() => {
    if (!formData || durationModalAlreadyPrompted) return;
    if ((formData?.canEdit ?? true) === false) return;

    const specs = ensureSpecObject(formData);
    const hasDuration = Boolean(formData.duration || specs.duration);

    if (isTimeBasedArtwork(formData) && !hasDuration) {
      setShowDurationModal(true);
      setDurationModalAlreadyPrompted(true);
    }
  }, [formData, durationModalAlreadyPrompted]);

  // -----------------------------------------------------------
  // Chargement de l’œuvre (proprio → sinon partagée + can_edit)
  // -----------------------------------------------------------
  useEffect(() => {
    if (!user?.id || !artworkId) return;

    async function fetchArtwork() {
      setIsLoading(true);
      setError(null);

      try {
        // 1) Charger l’œuvre via la vue sécurisée.
        // IMPORTANT : ne pas filtrer par client_id ici.
        // La vue artworks_secure décide déjà si l'utilisateur courant peut voir l'œuvre.
        // Un expert autorisé n'est pas le propriétaire, donc .eq("client_id", user.id)
        // le bloquerait à tort.
        let { data: secureArtwork, error: secureErr } = await supabase
          .from("artworks_secure")
          .select("*")
          .eq("id", artworkId)
          .maybeSingle();

        if (secureErr) throw secureErr;

        // Si artworks_secure retourne null (RLS bloque pour un expert non-propriétaire),
        // on tente directement v_expert_artworks avant d'abandonner.
        if (!secureArtwork) {
          const { data: expertFallback } = await supabase
            .from("v_expert_artworks")
            .select("*")
            .eq("id", artworkId)
            .maybeSingle();
          if (expertFallback) {
            secureArtwork = expertFallback;
            // can_edit vient de la vue expert (IS NOT FALSE = null traité comme true)
            secureArtwork._expertCanEdit = expertFallback.can_edit !== false;
          }
        }

        let baseData = secureArtwork;
        let isShared = Boolean(secureArtwork && secureArtwork.client_id !== user.id);
        let ownerId = secureArtwork?.client_id || null;
        // Propriétaire → toujours canEdit. Expert via fallback → lire can_edit de la vue.
        let canEdit = secureArtwork?._expertCanEdit !== undefined
          ? secureArtwork._expertCanEdit
          : Boolean(secureArtwork && secureArtwork.client_id === user.id);

        // Hydrater la localisation séparément.
        // PostgREST ne peut pas automatiquement faire location:location_id(...) sur une vue
        // sans relation FK déclarée entre la vue et locations.
        if (baseData?.location_id) {
          const { data: locationRow, error: locationErr } = await supabase
            .from("locations")
            .select("id, name, address")
            .eq("id", baseData.location_id)
            .maybeSingle();

          if (!locationErr) {
            baseData.location = locationRow || null;
          }
        }

        // 2) Si l'utilisateur n'est pas propriétaire, récupérer ses droits sur la collection.
        if (baseData && baseData.client_id !== user.id && baseData.collection_id) {
          const { data: permissionRow, error: permissionErr } = await supabase
            .from("collection_permissions")
            .select(
              `
              can_view,
              can_edit,
              can_view_financial,
              can_view_location,
              can_view_documents,
              can_view_provenance,
              can_view_transmission,
              can_view_private_notes
            `
            )
            .eq("collection_id", baseData.collection_id)
            .eq("grantee_id", user.id)
            .maybeSingle();

          if (permissionErr) throw permissionErr;

          if (permissionRow?.can_view) {
            isShared = true;
            canEdit = Boolean(permissionRow.can_edit);
            baseData = {
              ...baseData,
              ...permissionRow,
            };
          } else {
            // Si aucune permission collection directe, on tente l'ancienne vue expert.
            const { data: sharedRow, error: sharedErr } = await supabase
              .from("v_expert_artworks")
              .select("*")
              .eq("id", artworkId)
              .maybeSingle();

            if (sharedErr) throw sharedErr;

            if (sharedRow) {
              isShared = true;
              canEdit = Boolean(sharedRow.can_edit);
              baseData = {
                ...baseData,
                ...sharedRow,
              };
              ownerId = sharedRow.client_id || sharedRow.owner_id || ownerId;
            }
          }
        }

        // 3) Tentative collaborateur
        if (!baseData) {
          const { data: collabRow } = await supabase
            .from("collaborators")
            .select("id, can_view, can_edit, collection_id, status, created_by")
            .eq("user_id", user.id)
            .eq("status", "active")
            .maybeSingle();

          if (collabRow?.can_view) {
            const { data: collabAws } = await supabase
              .rpc("get_collaborator_artwork", {
                p_collaborator_id: collabRow.id,
                p_artwork_id: artworkId,
              });

            if (collabAws && collabAws.length > 0) {
              baseData = collabAws[0];
              isShared = true;
              // can_edit peut être null en DB — traiter null comme true si can_view est accordé
              canEdit = collabRow.can_edit !== false;
              ownerId = baseData.client_id;
              // Stocker les infos collaborateur pour les logs
              baseData._collabId = collabRow.id;
              baseData._collabCreatedBy = collabRow.created_by;
              collabIdRef.current = collabRow.id;
            }
          }
        }

        if (!baseData) {
          setError(t("artworkEdit.edit.notFound"));
          setArtwork(null);
          setFormData(null);
          return;
        }

        const data = baseData;

        // --- Normalisation acquisitionDetails (import CSV) ---
        const rawAcq = safeParse(
          data.acquisitionDetails,
          data.acquisitionDetails || data.acquisition_details || {}
        );

        const normalizedAcq = { ...rawAcq };

        if (!normalizedAcq.date && normalizedAcq.acquisition_date) {
          normalizedAcq.date = normalizedAcq.acquisition_date;
        }
        if (!normalizedAcq.price && normalizedAcq.acquisition_price) {
          normalizedAcq.price = normalizedAcq.acquisition_price;
        }
        if (!normalizedAcq.source && normalizedAcq.acquisition_source) {
          normalizedAcq.source = normalizedAcq.acquisition_source;
        }
        if (!normalizedAcq.type && normalizedAcq.acquisition_type) {
          normalizedAcq.type = normalizeAcquisitionType(normalizedAcq.acquisition_type);
        }

        // Hydratation depuis les colonnes plates actuelles public.artworks.
        if (!normalizedAcq.date && data.acquisition_date) {
          normalizedAcq.date = data.acquisition_date;
        }
        if (!normalizedAcq.price && data.acquisition_price != null) {
          normalizedAcq.price = data.acquisition_price;
        }
        if (!normalizedAcq.source && data.acquisition_source) {
          normalizedAcq.source = data.acquisition_source;
        }
        if (!normalizedAcq.type && data.acquisition_type) {
          normalizedAcq.type = normalizeAcquisitionType(data.acquisition_type);
        }

        if (!normalizedAcq.auction_house && data.auction_house) {
          normalizedAcq.auction_house = data.auction_house;
        }
        if (!normalizedAcq.type && data.auction_house) {
          normalizedAcq.type = "auction";
        }
        if (!normalizedAcq.auction_sale_name && data.auction_sale_name) {
          normalizedAcq.auction_sale_name = data.auction_sale_name;
        }
        if (!normalizedAcq.auction_sale_date && data.auction_sale_date) {
          normalizedAcq.auction_sale_date = data.auction_sale_date;
        }
        if (!normalizedAcq.auction_lot_number && data.auction_lot_number) {
          normalizedAcq.auction_lot_number = data.auction_lot_number;
          normalizedAcq.lot_number = data.auction_lot_number;
        }
        if (!normalizedAcq.auction_hammer_price && data.auction_hammer_price != null) {
          normalizedAcq.auction_hammer_price = data.auction_hammer_price;
          normalizedAcq.hammer_price = data.auction_hammer_price;
        }
        if (!normalizedAcq.auction_fees && data.auction_fees != null) {
          normalizedAcq.auction_fees = data.auction_fees;
        }
        if (!normalizedAcq.lot_total && data.acquisition_price != null) {
          normalizedAcq.lot_total = data.acquisition_price;
        }
        normalizedAcq.type = normalizeAcquisitionType(normalizedAcq.type);
        normalizedAcq.acquisition_type = normalizedAcq.type;

        // --- Normalisation des spécifications / dimensions ---
        const rawSpecs = safeParse(
          data.specifications,
          data.specifications || {}
        );
        const rawDims = (rawSpecs && rawSpecs.dimensions) || {};

        const parsedFromString = parseDimensionsString(data.dimensions);

        const normalizedDims = {
          unit:
            (rawDims && rawDims.unit) ||
            data.dimensions_unit ||
            data.unit ||
            "cm",
          height:
            (rawDims &&
            rawDims.height !== undefined &&
            rawDims.height !== ""
              ? Number(rawDims.height)
              : null) ??
            (data.dimensions_height != null ? data.dimensions_height : null) ??
            (parsedFromString.height != null ? parsedFromString.height : null) ??
            (data.height != null ? data.height : null),
          width:
            (rawDims &&
            rawDims.width !== undefined &&
            rawDims.width !== ""
              ? Number(rawDims.width)
              : null) ??
            (data.dimensions_width != null ? data.dimensions_width : null) ??
            (parsedFromString.width != null ? parsedFromString.width : null) ??
            (data.width != null ? data.width : null),
          depth:
            (rawDims &&
            rawDims.depth !== undefined &&
            rawDims.depth !== ""
              ? Number(rawDims.depth)
              : null) ??
            (data.dimensions_depth != null ? data.dimensions_depth : null) ??
            (parsedFromString.depth != null ? parsedFromString.depth : null) ??
            (data.depth != null ? data.depth : null),
        };
        // --- Normalisation framedSize / paperSize / duration ---
        const rawFramed = (rawSpecs && rawSpecs.framedSize) || {};
        const rawPaper = (rawSpecs && rawSpecs.paperSize) || {};

        const normalizedFramed = {
          unit:
            rawFramed.unit ||
            data.framed_unit ||
            normalizedDims.unit ||
            "cm",
          height:
            (rawFramed.height !== undefined && rawFramed.height !== ""
              ? Number(rawFramed.height)
              : null) ?? (data.framed_height != null ? data.framed_height : null),
          width:
            (rawFramed.width !== undefined && rawFramed.width !== ""
              ? Number(rawFramed.width)
              : null) ?? (data.framed_width != null ? data.framed_width : null),
          depth:
            (rawFramed.depth !== undefined && rawFramed.depth !== ""
              ? Number(rawFramed.depth)
              : null) ?? (data.framed_depth != null ? data.framed_depth : null),
        };

        const normalizedPaper = {
          unit:
            rawPaper.unit ||
            data.paper_unit ||
            normalizedDims.unit ||
            "cm",
          height:
            (rawPaper.height !== undefined && rawPaper.height !== ""
              ? Number(rawPaper.height)
              : null) ?? (data.paper_height != null ? data.paper_height : null),
          width:
            (rawPaper.width !== undefined && rawPaper.width !== ""
              ? Number(rawPaper.width)
              : null) ?? (data.paper_width != null ? data.paper_width : null),
        };

        const hasFramedValues =
          normalizedFramed.height != null ||
          normalizedFramed.width != null ||
          normalizedFramed.depth != null;

        const hasPaperValues =
          normalizedPaper.height != null || normalizedPaper.width != null;

        const normalizedSpecs = {
          ...rawSpecs,
          dimensions: normalizedDims,
          framedSize: hasFramedValues ? normalizedFramed : null,
          paperSize: hasPaperValues ? normalizedPaper : null,
          // duration : on prend d'abord ce qui est en JSON, puis la colonne à plat
          duration:
            (rawSpecs && rawSpecs.duration != null
              ? rawSpecs.duration
              : data.duration) || "",
        };


       const signatureInfo = safeParse(data.signature_info, data.signature_info || {});

const parsed = {
  ...data,
  isShared,
  ownerId,
  canEdit: Boolean(canEdit),  // forcer boolean pour éviter null/undefined
  tags: data.tags || [],
  location: data.location || null,
  documents: safeParse(data.documents, data.documents || []),
  images: safeParse(data.images, data.images || []),
  provenance: safeParse(data.provenance, data.provenance || []),
  specifications: normalizedSpecs,
  acquisitionDetails: normalizedAcq,

  // Compat UI legacy : certains composants lisent encore mediumCategory,
  // mais la sauvegarde repartira toujours en medium_category.
  mediumCategory: data.medium_category || data.mediumCategory || "",

  // Hydratation UI SignatureSection depuis JSONB + fallback colonnes existantes
  signature_info: signatureInfo,
  signature_details:
    data.signature_details ||
    signatureInfo.signature_details ||
    data.signature_desc ||
    "",

  authentication_details:
    data.authentication_details ||
    signatureInfo.authentication_details ||
    data.certificate_authenticity ||
    "",

  signature_notes:
    data.signature_notes ||
    signatureInfo.signature_notes ||
    "",
};

        initialRef.current = parsed;
        setArtwork(parsed);
        setFormData(parsed);
      } catch (e) {
        console.error("[ArtworkEdit] Erreur de chargement", e);
        setError(t("artworkEdit.edit.loadError"));
      } finally {
        setIsLoading(false);
      }
    }

    fetchArtwork();
  }, [artworkId, user?.id, navigate, t]);

  // -----------------------------------------------------------
  // Chargement des lieux de stockage pour le vrai propriétaire
  // -----------------------------------------------------------
  useEffect(() => {
    if (!artwork || !user?.id) return;

    async function fetchLocations() {
      try {
        const clientId = artwork.ownerId || user.id;
        const { data, error } = await supabase
          .from("locations")
          .select("*")
          .eq("client_id", clientId);

        if (error) throw error;
        setLocations(data || []);
      } catch (e) {
        console.error("[ArtworkEdit] Erreur chargement locations", e);
      }
    }

    fetchLocations();
  }, [artwork, user?.id]);

  // -----------------------------------------------------------
  // Handlers généraux
  // -----------------------------------------------------------
  const handleFieldChange = (patch) => {
    setFormData((prev) => ({
      ...prev,
      ...patch,
    }));
  };

  const handleChangeLocation = (location) => {
    setFormData((prev) => ({
      ...prev,
      location_id: location?.id || null,
      location,
    }));
  };

  const handleSubmit = async () => {
    if (!formData || !artwork) return;
if (formData?.isShared && !formData?.canEdit && !collabIdRef.current) {
  alert(t("artworkEdit.edit.noEditRights"));
  return;
}
console.log("[ArtworkEdit] canEdit check:", { isShared: formData?.isShared, canEdit: formData?.canEdit, collabId: collabIdRef.current });

    if (saveInProgress) return;
    
    try {
      setSaveInProgress(true);
      const payload = cleanDataBeforeSubmit(formData);

      // Experts (isShared + canEdit) → RPC SECURITY DEFINER pour contourner RLS.
      // Propriétaires → update direct.
      let updatedRow = null;
      let updateErr = null;

      console.log("[ArtworkEdit] handleSubmit routing:", {
        isShared: formData?.isShared,
        canEdit: formData?.canEdit,
        currency: payload?.currency,
        artworkId,
      });

      if (formData?.isShared && formData?.canEdit) {
        // Chemin expert : on n'envoie que les champs gérés par la RPC
        // acquisition_price_eur et exchange_rate peuvent vivre dans acquisitionDetails
        const acqDet = payload.acquisitionDetails || {};
        const expertPayload = {
          // Acquisition & devise
          currency:                  payload.currency                  ?? null,
          acquisition_price:         payload.acquisition_price         ?? acqDet.acquisition_price         ?? null,
          acquisition_price_eur:     payload.acquisition_price_eur     ?? acqDet.acquisition_price_eur     ?? null,
          acquisition_exchange_rate: payload.acquisition_exchange_rate ?? acqDet.acquisition_exchange_rate ?? null,
          acquisition_type:          payload.acquisition_type          ?? null,
          acquisition_source:        payload.acquisition_source        ?? null,
          acquisition_date:          payload.acquisition_date          ?? null,
          acquisitionDetails:        payload.acquisitionDetails        ?? null,
          auction_hammer_price:      payload.auction_hammer_price      ?? null,
          auction_fees:              payload.auction_fees              ?? null,
          auction_house:             payload.auction_house             ?? null,
          auction_sale_name:         payload.auction_sale_name         ?? null,
          auction_sale_date:         payload.auction_sale_date         ?? null,
          auction_lot_number:        payload.auction_lot_number        ?? null,
          // Signature & certificat
          signature:                 payload.signature                 ?? null,
          signature_desc:            payload.signature_desc            ?? null,
          signature_info:            payload.signature_info            ?? null,
          certificate_auth:          payload.certificate_auth          ?? null,
          certificate_authenticity:  payload.certificate_authenticity  ?? null,
          // Propriété à vérifier
          ownership_verification:      payload.ownership_verification      ?? null,
          ownership_verification_note: payload.ownership_verification_note ?? null,
          // État de conservation
          condition:                 payload.condition                 ?? null,
          conditionNotes:            payload.conditionNotes            ?? null,
          // Description
          description:               payload.description              ?? null,
          // Provenance (text)
          provenance:                payload.provenance != null
            ? (typeof payload.provenance === "string"
                ? payload.provenance
                : JSON.stringify(payload.provenance))
            : null,
          // Images
          images:                    payload.images                    ?? null,
          primary_image_path:        payload.primary_image_path        ?? null,
          // Valeur / estimation — synchroniser les 3 champs ensemble
          // pour que la saisie manuelle dans le formulaire prime sur
          // l'estimation automatique (EstimationSemblables).
          estimated_value:           payload.estimated_value           ?? null,
          current_estimated_value:   payload.estimated_value           ?? null,
          valuation_estimated_value: payload.estimated_value           ?? null,
          valuation_consensus:       payload.estimated_value           ?? null,
        };
        // Supprimer les clés null pour ne pas écraser des données existantes
        Object.keys(expertPayload).forEach(k => {
          if (expertPayload[k] === null) delete expertPayload[k];
        });

        console.log("[ArtworkEdit] expertPayload envoyé RPC:", JSON.stringify(expertPayload));
        const { data: rpcResult, error: rpcErr } = await supabase
          .rpc("update_artwork_as_collaborator", {
            p_artwork_id: artworkId,
            p_payload: expertPayload,
          });
        console.log("[ArtworkEdit] RPC result:", rpcResult, "err:", rpcErr);
        if (rpcErr || !rpcResult?.success) {
          updateErr = rpcErr || { message: rpcResult?.error || "Échec RPC expert." };
        } else {
          updatedRow = { id: artworkId };
        }
      } else {
        // Chemin propriétaire : update direct. Enregistrer la fiche = elle n'est
        // plus une pré-fiche → on lève le statut « brouillon ».
        const { data: directRow, error: directErr } = await supabase
          .from("artworks")
          .update({ ...payload, is_draft: false })
          .eq("id", artworkId)
          .select("id, description, updated_at")
          .maybeSingle();
        updatedRow = directRow;
        updateErr = directErr;
      }

      if (updateErr || !updatedRow?.id) {
        const finalErr = updateErr || { message: t("artworkEdit.edit.noRowUpdated") };
        console.error("[ArtworkEdit] Erreur update", finalErr);
        alert(t("artworkEdit.edit.saveErrorDetailed", { message: finalErr?.message || "" }));
        return;
      }
      // ✅ LOG CHANGE (historique fiche)
      try {
        const before = initialRef.current || {};
        const after = formData || {};

        const pick = (o) => ({
          title: o.title ?? null,
          artist: o.artist ?? null,
          year: o.year ?? null,
          medium: o.medium ?? null,
          description: o.description ?? null,
          value: o.value ?? null,
          estimated_value: o.estimated_value ?? null,
          currency: o.currency ?? null,
          location_id: o.location_id ?? null,
          tags: Array.isArray(o.tags) ? o.tags : o.tags ?? null,
          documents: o.documents ?? null,
          images: o.images ?? null,
          provenance: o.provenance ?? null,
          specifications: o.specifications ?? null,
          acquisitionDetails: o.acquisitionDetails ?? null,
        });

        const A = pick(before);
        const B = pick(after);

        const toStr = (v) => {
          if (v == null) return null;
          if (typeof v === "string") return v;
          try { return JSON.stringify(v); } catch { return String(v); }
        };

        const changes = [];
        for (const k of Object.keys(A)) {
          const a = toStr(A[k]);
          const b = toStr(B[k]);
          if (a !== b) changes.push({ field: k, old: A[k] ?? null, new: B[k] ?? null });
        }

        if (changes.length > 0) {
          await supabase.rpc("log_artwork_change", {
            p_artwork_id: artworkId,
            p_action: "update",
            p_source: "artwork_edit",
            p_payload: { changes },
          });
        }
      } catch (e) {
        console.warn("[ArtworkEdit] log_artwork_change(update) failed", e?.message || e);
      }

      // ✅ LOG EDIT (après update OK, avant navigation)
      try {
        await supabase.rpc("log_artwork_access", {
          p_artwork_id: artworkId,
          p_action: "edit",
          // Le collaborateur apparaît comme "Team" de l'expert dans l'historique
          // (pas nommément — confidentialité)
        });
      } catch (e) {
        // ne bloque jamais l'UI
        console.warn("[ArtworkEdit] log_artwork_access(edit) failed", e?.message || e);
      }

      alert(t("artworkEdit.edit.updated"));
      navigate(`/artwork/${artworkId}`);

    } catch (e) {
      console.error("[ArtworkEdit] Erreur submit", e);
      alert(t("artworkEdit.edit.saveError"));
    } finally {
      setSaveInProgress(false);
    }
  };

  // -----------------------------------------------------------
  // Handlers IMAGES (upload + primary + suppression)
  // -----------------------------------------------------------
// REMPLACER TOUTES les fonctions de gestion d'images dans ArtworkEdit.jsx
// Autour des lignes 567-660

// ==========================================
// 1. UPLOAD D'IMAGES (avec sauvegarde immédiate)
// ==========================================
const handleAddImages = async (files) => {
  if (!files || files.length === 0) return;
if (formData?.isShared && !formData?.canEdit && !collabIdRef.current) {
  alert(t("artworkEdit.edit.noEditRights"));
  return;
}

  if (!artworkId) return;

  try {
    setSaveInProgress(true);

    // Upload des fichiers vers Supabase Storage
    const uploadedImages = await Promise.all(
      files.map(async (file) => {
        const ext = file.name.split(".").pop();
        const filePath = `${artworkId}/${Date.now()}-${Math.random()
          .toString(36)
          .slice(2)}.${ext}`;

        const { error: uploadErr } = await supabase.storage
          .from("artworks-images")
          .upload(filePath, file, { upsert: true });

        if (uploadErr) {
          console.error("[ArtworkEdit] Erreur upload image", uploadErr);
          throw uploadErr;
        }

        const { data: publicURL } = supabase.storage
          .from("artworks-images")
          .getPublicUrl(filePath);

        return {
          id: `img_${Date.now()}_${Math.random().toString(36).slice(2)}`,
          path: publicURL.publicUrl,
          url: publicURL.publicUrl,
          publicUrl: publicURL.publicUrl,
          isPrimary: false,
        };
      })
    );

    // Récupérer images actuelles
    const currentImages = formData?.images || [];
    const isPrimaryExists = currentImages.some((img) => img.isPrimary);

    let newImages = [...uploadedImages];
    if (!isPrimaryExists && newImages.length > 0 && currentImages.length === 0) {
      newImages[0].isPrimary = true;
    }

    // Combiner
    const allImages = [...currentImages, ...newImages];

    // ✨ Sauvegarder IMMÉDIATEMENT en base
const { error: saveError } = await supabase
  .from("artworks")
  .update({
    images: allImages,
    image_url: allImages.find(img => img.isPrimary)?.url || allImages[0]?.url || null
  })
  .eq("id", artworkId);

    if (saveError) {
      console.error("[ArtworkEdit] Erreur sauvegarde images", saveError);
      throw saveError;
    }

    // Mettre à jour le state
    setFormData((prev) => ({
      ...prev,
      images: allImages,
      image_url: allImages.find(img => img.isPrimary)?.url || allImages[0]?.url || null
    }));
try {
  await supabase.rpc("log_artwork_access", {
    p_artwork_id: artworkId,
    p_action: "edit",
  });
} catch (e) {
  console.warn("[ArtworkEdit] log_artwork_access(edit) failed", e?.message || e);
}

    console.log("✅ Images uploadées ET sauvegardées");
  } catch (error) {
    console.error("Erreur upload images:", error);
    alert(t("artworkEdit.edit.imageUploadError"));
  } finally {
    setSaveInProgress(false);
  }
};

// ==========================================
// 2. SUPPRESSION D'IMAGE (avec sauvegarde immédiate)
// ==========================================
const handleRemoveImage = async (imageId) => {
if (formData?.isShared && !formData?.canEdit && !collabIdRef.current) {
  alert(t("artworkEdit.edit.noEditRights"));
  return;
}

  if (!artworkId) return;

  try {
    setSaveInProgress(true);

    const current = formData?.images || [];
    const filtered = current.filter((img) => img.id !== imageId);

    // S'il ne reste plus d'image primaire → forcer la première
    if (filtered.length > 0 && !filtered.some((img) => img.isPrimary)) {
      filtered[0] = { ...filtered[0], isPrimary: true };
    }

    // ✨ Sauvegarder IMMÉDIATEMENT en base
    const { error: saveError } = await supabase
      .from("artworks")
      .update({
  images: filtered,
  image_url: filtered.find(img => img.isPrimary)?.url || filtered[0]?.url || null
})
      .eq("id", artworkId);

    if (saveError) {
      console.error("[ArtworkEdit] Erreur sauvegarde après suppression", saveError);
      throw saveError;
    }

    // Mettre à jour le state
    setFormData((prev) => ({
      ...prev,
      images: filtered,
      image_url: filtered.find(img => img.isPrimary)?.url || filtered[0]?.url || null
    }));
try {
  await supabase.rpc("log_artwork_access", {
    p_artwork_id: artworkId,
    p_action: "edit",
  });
} catch (e) {
  console.warn("[ArtworkEdit] log_artwork_access(edit) failed", e?.message || e);
}

    console.log("✅ Image supprimée ET sauvegardée");
  } catch (error) {
    console.error("Erreur suppression image:", error);
    alert(t("artworkEdit.edit.imageDeleteError"));
  } finally {
    setSaveInProgress(false);
  }
};

// ==========================================
// 3. DÉFINIR IMAGE PRINCIPALE (avec sauvegarde immédiate)
// ==========================================
const handleSetPrimaryImage = async (imageId) => {
if (formData?.isShared && !formData?.canEdit && !collabIdRef.current) {
  alert(t("artworkEdit.edit.noEditRights"));
  return;
}

  if (!artworkId) return;

  try {
    setSaveInProgress(true);

    const current = formData?.images || [];
    const updated = current.map((img) => ({
      ...img,
      isPrimary: img.id === imageId,
    }));

    // ✨ Sauvegarder IMMÉDIATEMENT en base
    const primaryImage = updated.find(img => img.isPrimary);
    const { error: saveError } = await supabase
      .from("artworks")
      .update({
        images: updated,
        image_url: primaryImage?.url || null,
      })
      .eq("id", artworkId);

    if (saveError) {
      console.error("[ArtworkEdit] Erreur sauvegarde image principale", saveError);
      throw saveError;
    }

    // Mettre à jour le state
    setFormData((prev) => ({
      ...prev,
      images: updated,
      image_url: primaryImage?.url || null,
    }));

    try {
      await supabase.rpc("log_artwork_access", {
        p_artwork_id: artworkId,
        p_action: "edit",
      });
    } catch (e) {
      console.warn("[ArtworkEdit] log_artwork_access(edit) failed", e?.message || e);
    }

    console.log("✅ Image principale définie ET sauvegardée");
  } catch (error) {
    console.error("Erreur définition image principale:", error);
    alert(t("artworkEdit.edit.imagePrimaryError"));
  } finally {
    setSaveInProgress(false);
  }
};


  const handleAddFrameSizeFromModal = (frameSize) => {
    setFormData((prev) => {
      const specs = ensureSpecObject(prev);
      return {
        ...prev,
        framed_height: frameSize?.height ?? null,
        framed_width: frameSize?.width ?? null,
        framed_depth: frameSize?.depth ?? null,
        specifications: {
          ...specs,
          framedSize: frameSize,
        },
      };
    });
    setShowFrameSizeModal(false);
  };

  const handleAddPaperSizeFromModal = (paperSize) => {
    setFormData((prev) => {
      const specs = ensureSpecObject(prev);
      return {
        ...prev,
        paper_height: paperSize?.height ?? null,
        paper_width: paperSize?.width ?? null,
        specifications: {
          ...specs,
          paperSize,
        },
      };
    });
    setShowPaperSizeModal(false);
  };

  const handleAddDurationFromModal = (duration) => {
    setFormData((prev) => {
      const specs = ensureSpecObject(prev);
      return {
        ...prev,
        duration,
        specifications: {
          ...specs,
          duration,
        },
      };
    });
    setShowDurationModal(false);
  };

  const handleAddDocumentFromModal = (document) => {
    setFormData((prev) => {
      const current = Array.isArray(prev?.documents) ? prev.documents : [];
      return {
        ...prev,
        documents: [...current, document],
      };
    });
    setShowDocumentModal(false);
  };

  const handleAddUploadedImagesFromModal = (images) => {
    const incoming = Array.isArray(images) ? images : [];
    if (incoming.length === 0) {
      setShowImageUploadModal(false);
      return;
    }

    setFormData((prev) => {
      const current = Array.isArray(prev?.images) ? prev.images : [];
      const alreadyHasPrimary = current.some((img) => img?.isPrimary);
      const nextIncoming = incoming.map((img, index) => ({
        ...img,
        isPrimary: alreadyHasPrimary ? !!img.isPrimary : index === 0,
      }));
      const nextImages = [...current, ...nextIncoming];
      return {
        ...prev,
        images: nextImages,
        image_url: nextImages.find((img) => img?.isPrimary)?.url || nextImages[0]?.url || null,
      };
    });
    setShowImageUploadModal(false);
  };

  // -----------------------------------------------------------
  // Rendu
  // -----------------------------------------------------------
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-gray-500">{t("artworkEdit.edit.loading")}</p>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-red-500">
          {error || t("artworkEdit.edit.notFound")}
        </p>
      </div>
    );
  }

  const activeTab = tabs[selectedTabIndex]?.id;
  const canEdit = formData?.canEdit ?? true;
  const maxImages = getMaxImagesForProfile(profile);

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* HEADER */}
      <header className="flex justify-between items-center border-b border-gray-200 pb-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold">
            {t("artworkEdit.edit.editTitle", { title: formData.title || t("artworkEdit.edit.untitled") })}
          </h1>
          {formData.isShared && (
            <p className="text-xs text-gray-500 mt-1">
              {t("artworkEdit.edit.sharedNotice")}
            </p>
          )}
        </div>

        <IconButtons
          isEditMode={true}
          backUrl={`/artwork/${artworkId}`}
          onSave={handleSubmit}
        />
      </header>

      {/* TABS HEADER */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-6">
          {tabs.map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => setSelectedTabIndex(index)}
              className={`whitespace-nowrap pb-3 text-sm font-medium border-b-2 ${
                activeTab === tab.id
                  ? "border-black text-black"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* TABS CONTENT */}
      <div className="space-y-6">
        {/* ONGLET : DÉTAILS */}
        {activeTab === "details" && (
          <ArtworkDetailTab
            formData={formData}
            setFormData={setFormData}
            readOnly={!canEdit}
          />
        )}

        {/* ONGLET : SPÉCIFICATIONS + IMAGES */}
        {activeTab === "specifications" && (
          <div className="space-y-6">
            <ArtworkSpecificationsTab
              formData={formData}
              setFormData={setFormData}
              readOnly={!canEdit}
              onAddFrameSize={() => setShowFrameSizeModal(true)}
              onAddPaperSize={() => setShowPaperSizeModal(true)}
              onAddDuration={() => setShowDurationModal(true)}
              isTimeBasedArtwork={isTimeBasedArtwork(formData)}
              isPaperArtwork={isPaperArtwork(formData)}
            />

            <ArtworkImageManager
              images={formData.images || []}
              readOnly={!canEdit}
              onOpenImageUpload={() => setShowImageUploadModal(true)}
              onAddImages={handleAddImages}
              onSetPrimaryImage={handleSetPrimaryImage}
              onRemoveImage={handleRemoveImage}
              maxImages={maxImages}
            />

            {canEdit && (
              <button
                type="button"
                onClick={() => setShowPhoneImg(true)}
                className="mt-3 text-sm flex items-center gap-1 hover:underline"
                style={{ color: "#8a6d1f" }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
                </svg>
                {t("usePhone.title")}
              </button>
            )}

            <UseMyPhoneModal
              open={showPhoneImg}
              onClose={() => setShowPhoneImg(false)}
              artworkId={artworkId}
              actionType="upload_image"
              typeOptions={[
                { value: "verso", label: t("mobileCapture.doc.verso") },
                { value: "signature", label: t("mobileCapture.doc.signature") },
                { value: "detail", label: t("mobileCapture.doc.detail") },
                { value: "main", label: t("mobileCapture.doc.main") },
              ]}
              onCompleted={async () => {
                // Re-sync depuis la base : le mobile a écrit artworks.images ;
                // on rafraîchit le form-state pour éviter tout écrasement ultérieur.
                const { data } = await supabase
                  .from("artworks")
                  .select("images, image_url")
                  .eq("id", artworkId)
                  .maybeSingle();
                if (data) {
                  setFormData((prev) => ({
                    ...prev,
                    images: data.images || [],
                    image_url: data.image_url ?? prev?.image_url,
                  }));
                }
              }}
            />
          </div>
        )}

        {/* ONGLET : PROVENANCE */}
        {activeTab === "provenance" && (
          <ArtworkProvenanceAcquisitionTab
            artwork={formData}
            formData={formData}
            setFormData={setFormData}
            readOnly={!canEdit}
          />
        )}

        {/* ONGLET : DOCUMENTS */}
        {activeTab === "documents" && (
          <>
            {canEdit && (
              <ZipImportButton
                artworkId={artworkId}
                formData={formData}
                setFormData={setFormData}
              />
            )}
            <ArtworkDocumentsTab
              formData={formData}
              setFormData={setFormData}
              readOnly={!canEdit}
              onAddDocument={() => setShowDocumentModal(true)}
            />
          </>
        )}

        {/* ONGLET : EMPLACEMENT — masqué pour collaborateurs (confidentialité) */}
        {activeTab === "location" && formData?._collabId && (
          <div style={{ padding:"32px 0", textAlign:"center", color:"#6b6b6b", fontFamily:"system-ui,sans-serif" }}>
            <p style={{ fontFamily:"Georgia,serif", fontSize:18, color:"#1a1a1a", marginBottom:8 }}>
              {t("artworkEdit.edit.confidentialLocationTitle")}
            </p>
            <p style={{ fontSize:14, lineHeight:1.7, maxWidth:420, margin:"0 auto" }}>
              {t("artworkEdit.edit.confidentialLocationBody")}
            </p>
          </div>
        )}
        {activeTab === "location" && !formData?._collabId && (
          <>
            <ArtworkLocationTab
              formData={formData}
              onChange={handleFieldChange}
              locations={locations}
              onChangeLocation={() => setShowLocationModal(true)}
              readOnly={!canEdit}
            />

            {showLocationModal && (
              <LocationModal
                artwork={formData}
                onChangeLocation={(locId) => {
                  handleChangeLocation(locations.find((l) => l.id === locId) || null);
                  setShowLocationModal(false);
                }}
                onClose={() => setShowLocationModal(false)}
              />
            )}
          </>
        )}

        {/* ONGLET : NFT / CERTIFICAT BLOCKCHAIN */}
        {activeTab === "nft" && (
          <ArtworkNFTTab artwork={formData} readOnly={!canEdit} />
        )}
                {/* ONGLET : EXPERTISE CUP — réservé aux experts */}
        {activeTab === "expertise" && isExpert && (
          <ArtworkComparables
            formData={formData}
            setFormData={setFormData}
            artworkId={artworkId}
            readOnly={false}
          />
        )}

        {/* CONSENTEMENT STATS */}
        <div className="mt-10 border-t border-gray-200 pt-6">
          <h2 className="text-sm font-semibold mb-2">
            {t("artworkEdit.edit.consentTitle")}
          </h2>
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              className="mt-1"
              checked={!!formData.allow_in_stats}
              onChange={async (e) => {
                const newValue = e.target.checked;
                setFormData((prev) => ({
                  ...prev,
                  allow_in_stats: newValue,
                }));

                const { error: updErr } = await supabase
                  .from("artworks")
                  .update({ allow_in_stats: newValue })
                  .eq("id", artworkId);

                if (updErr) {
                  console.error(
                    "[ArtworkEdit] Erreur mise à jour allow_in_stats",
                    updErr
                  );
                }
              }}
            />
            <span className="text-sm text-gray-700">
              {t("artworkEdit.edit.consentText")}
              <br />
              <em className="text-gray-500">
                {t("artworkEdit.edit.consentNote")}
              </em>
            </span>
          </label>
        </div>

        {/* ACTIONS bas de page */}
        <div className="flex justify-end mt-8 space-x-4">
          <button
            type="button"
            onClick={() => navigate(`/artwork/${artworkId}`)}
            className="px-4 py-2 text-sm border border-gray-300 rounded-full hover:bg-gray-50"
          >
            {t("artworkEdit.edit.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saveInProgress}
            className="px-5 py-2 text-sm rounded-full bg-black text-white hover:bg-gray-900 disabled:opacity-60"
          >
            {saveInProgress ? t("artworkEdit.edit.saving") : t("artworkEdit.edit.save")}
          </button>
        </div>

        {error && (
          <p className="mt-4 text-sm text-red-600">
            {error}
          </p>
        )}
      </div>

      {showImageUploadModal && (
        <ImageUploadModal
          onClose={() => setShowImageUploadModal(false)}
          onAddImages={handleAddUploadedImagesFromModal}
        />
      )}

      {showFrameSizeModal && (
        <FrameSizeModal
          dimensions={formData?.specifications?.dimensions || {
            height: formData?.dimensions_height ?? formData?.height,
            width: formData?.dimensions_width ?? formData?.width,
            depth: formData?.dimensions_depth ?? formData?.depth,
            unit: formData?.dimensions_unit ?? formData?.unit ?? "cm",
          }}
          onClose={() => setShowFrameSizeModal(false)}
          onSubmit={handleAddFrameSizeFromModal}
        />
      )}

      {showPaperSizeModal && (
        <PaperSizeModal
          dimensions={formData?.specifications?.dimensions || {
            height: formData?.dimensions_height ?? formData?.height,
            width: formData?.dimensions_width ?? formData?.width,
            unit: formData?.dimensions_unit ?? formData?.unit ?? "cm",
          }}
          onClose={() => setShowPaperSizeModal(false)}
          onSubmit={handleAddPaperSizeFromModal}
        />
      )}

      {showDurationModal && (
        <DurationModal
          onClose={() => setShowDurationModal(false)}
          onSubmit={handleAddDurationFromModal}
        />
      )}

      {showDocumentModal && (
        <DocumentModal
          artworkId={artworkId}
          onClose={() => setShowDocumentModal(false)}
          onAddDocument={handleAddDocumentFromModal}
        />
      )}

    </div>
  );
};

export default ArtworkEdit;